import type { MarketPhase, SessionInfo } from '@/lib/market/types'

export const DAY_MS = 86400000
export const EAT_OFFSET_MS = 3 * 3600000
export const PRE_OPEN_MINUTE = 9 * 60
export const OPEN_MINUTE = 9 * 60 + 30
export const CLOSE_MINUTE = 15 * 60
export const SESSION_MINUTES = CLOSE_MINUTE - OPEN_MINUTE
export const SESSION_STEPS = 66
export const ORIGIN_DATE = '2023-01-02'
export const ANCHOR_DATE = '2026-06-30'

export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const FIXED_HOLIDAYS: Array<[number, number, string]> = [
    [1, 1, "New Year's Day"],
    [5, 1, 'Labour Day'],
    [6, 1, 'Madaraka Day'],
    [10, 10, 'Mazingira Day'],
    [10, 20, 'Mashujaa Day'],
    [12, 12, 'Jamhuri Day'],
    [12, 25, 'Christmas Day'],
    [12, 26, 'Boxing Day']
]

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

export const dayFromMs = (ms: number): number => Math.floor((ms + EAT_OFFSET_MS) / DAY_MS)

export const msIntoDay = (ms: number): number => {
    const shifted = ms + EAT_OFFSET_MS

    return shifted - Math.floor(shifted / DAY_MS) * DAY_MS
}

export const dayToIso = (day: number): string => new Date(day * DAY_MS).toISOString().slice(0, 10)

export const isoToDay = (iso: string): number => {
    const [year, month, date] = iso.slice(0, 10).split('-').map(Number)

    return Math.floor(Date.UTC(year, (month || 1) - 1, date || 1) / DAY_MS)
}

export const dayParts = (day: number): { year: number, month: number, date: number, weekday: number } => {
    const value = new Date(day * DAY_MS)

    return {
        year: value.getUTCFullYear(),
        month: value.getUTCMonth(),
        date: value.getUTCDate(),
        weekday: value.getUTCDay()
    }
}

export const weekdayOf = (day: number): number => (((day + 4) % 7) + 7) % 7

const easterSunday = (year: number): number => {
    const a = year % 19
    const b = Math.floor(year / 100)
    const c = year % 100
    const d = Math.floor(b / 4)
    const e = b % 4
    const f = Math.floor((b + 8) / 25)
    const g = Math.floor((b - f + 1) / 3)
    const h = (19 * a + b - d - g + 15) % 30
    const i = Math.floor(c / 4)
    const k = c % 4
    const l = (32 + 2 * e + 2 * i - h - k) % 7
    const m = Math.floor((a + 11 * h + 22 * l) / 451)
    const month = Math.floor((h + l - 7 * m + 114) / 31)
    const date = ((h + l - 7 * m + 114) % 31) + 1

    return Math.floor(Date.UTC(year, month - 1, date) / DAY_MS)
}

const holidayCache = new Map<number, Map<number, string>>()

export const holidaysForYear = (year: number): Map<number, string> => {
    const cached = holidayCache.get(year)
    if (cached) return cached

    const holidays = new Map<number, string>()
    const fixedDays: Array<[number, string]> = []

    for (const [month, date, name] of FIXED_HOLIDAYS) {
        const day = Math.floor(Date.UTC(year, month - 1, date) / DAY_MS)
        holidays.set(day, name)
        fixedDays.push([day, name])
    }

    const easter = easterSunday(year)
    holidays.set(easter - 2, 'Good Friday')
    holidays.set(easter + 1, 'Easter Monday')

    for (const [day, name] of fixedDays) {
        if (weekdayOf(day) !== 0) continue

        let observed = day + 1
        while (holidays.has(observed) || weekdayOf(observed) === 0 || weekdayOf(observed) === 6) observed++
        holidays.set(observed, `${name} (observed)`)
    }

    holidayCache.set(year, holidays)

    return holidays
}

export const holidayName = (day: number): string | null => holidaysForYear(dayParts(day).year).get(day) ?? null

export const isTradingDay = (day: number): boolean => {
    const weekday = weekdayOf(day)
    if (weekday === 0 || weekday === 6) return false

    return holidayName(day) === null
}

export const previousTradingDay = (day: number): number => {
    let candidate = day - 1
    while (!isTradingDay(candidate)) candidate--

    return candidate
}

export const nextTradingDay = (day: number): number => {
    let candidate = day + 1
    while (!isTradingDay(candidate)) candidate++

    return candidate
}

export const tradingDaysBetween = (from: number, to: number): number[] => {
    const days: number[] = []

    for (let day = from; day <= to; day++) {
        if (isTradingDay(day)) days.push(day)
    }

    return days
}

export const indexOnOrBefore = (days: number[], target: number): number => {
    let low = 0
    let high = days.length - 1
    let found = -1

    while (low <= high) {
        const middle = (low + high) >> 1

        if (days[middle] <= target) {
            found = middle
            low = middle + 1
        }
        else {
            high = middle - 1
        }
    }

    return found
}

export const openTimeOf = (day: number): number => day * DAY_MS - EAT_OFFSET_MS + OPEN_MINUTE * 60000

export const closeTimeOf = (day: number): number => day * DAY_MS - EAT_OFFSET_MS + CLOSE_MINUTE * 60000

export const getSession = (now: number): SessionInfo => {
    const today = dayFromMs(now)
    const minute = msIntoDay(now) / 60000
    const tradingToday = isTradingDay(today)

    let phase: MarketPhase
    let day: number
    let progress = 1

    if (!tradingToday) {
        const weekday = weekdayOf(today)
        phase = weekday === 0 || weekday === 6 ? 'weekend' : 'holiday'
        day = previousTradingDay(today)
    }
    else if (minute < PRE_OPEN_MINUTE) {
        phase = 'pre-market'
        day = previousTradingDay(today)
    }
    else if (minute < OPEN_MINUTE) {
        phase = 'pre-open'
        day = previousTradingDay(today)
    }
    else if (minute < CLOSE_MINUTE) {
        phase = 'open'
        day = today
        progress = (minute - OPEN_MINUTE) / SESSION_MINUTES
    }
    else {
        phase = 'closed'
        day = today
    }

    const nextOpenDay = tradingToday && minute < OPEN_MINUTE ? today : nextTradingDay(today)

    return {
        day,
        date: dayToIso(day),
        today,
        phase,
        progress: clamp01(progress),
        now,
        nextOpen: openTimeOf(nextOpenDay),
        closesAt: phase === 'open' ? closeTimeOf(today) : null,
        holiday: phase === 'holiday' ? holidayName(today) : null
    }
}

export const lastTradingDayOfPreviousYear = (day: number): number => {
    const { year } = dayParts(day)
    const firstOfYear = Math.floor(Date.UTC(year, 0, 1) / DAY_MS)

    return previousTradingDay(firstOfYear)
}

export const formatDay = (day: number, style: 'short' | 'long' | 'compact' | 'weekday' | 'month' = 'short'): string => {
    const { year, month, date, weekday } = dayParts(day)

    if (style === 'long') return `${WEEKDAYS_LONG[weekday]} ${date} ${MONTHS_LONG[month]} ${year}`
    if (style === 'compact') return `${date} ${MONTHS_SHORT[month]}`
    if (style === 'weekday') return `${WEEKDAYS_SHORT[weekday]} ${date} ${MONTHS_SHORT[month]}`
    if (style === 'month') return `${MONTHS_SHORT[month]} ${year}`

    return `${date} ${MONTHS_SHORT[month]} ${year}`
}

export const formatIsoDate = (iso: string | null, style: 'short' | 'long' | 'compact' | 'weekday' | 'month' = 'short'): string => {
    if (!iso) return '-'

    return formatDay(isoToDay(iso), style)
}

const pad = (value: number): string => (value < 10 ? `0${value}` : `${value}`)

export const formatEatClock = (ms: number, withSeconds = true): string => {
    const into = msIntoDay(ms)
    const hours = Math.floor(into / 3600000)
    const minutes = Math.floor((into % 3600000) / 60000)
    const seconds = Math.floor((into % 60000) / 1000)

    return withSeconds ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}`
}

export const sessionStepLabel = (step: number): string => {
    const minutes = OPEN_MINUTE + step * (SESSION_MINUTES / SESSION_STEPS)
    const hours = Math.floor(minutes / 60)

    return `${pad(hours)}:${pad(Math.round(minutes % 60))}`
}

export const phaseLabel = (phase: MarketPhase): string => {
    if (phase === 'open') return 'Market open'
    if (phase === 'pre-open') return 'Pre-open session'
    if (phase === 'weekend') return 'Closed for the weekend'
    if (phase === 'holiday') return 'Closed for a public holiday'

    return 'Market closed'
}

export const formatNextOpen = (session: SessionInfo): string => {
    const day = dayFromMs(session.nextOpen)
    const label = day === session.today ? 'today' : day === session.today + 1 ? 'tomorrow' : formatDay(day, 'weekday')

    return `Opens ${label} at 09:30 EAT`
}
